import {COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY} from '@hconnect/common/consts'
import {appUrl} from '@hconnect/common/hproduce/config'
import {COCKPIT_HOST, PLANT_EVENTS} from '@hconnect/common/hproduce/config/cockpit'
import {SUMMARY_SEGMENT} from '@hconnect/common/hproduce/consts'
import {DateRange, EventsTab, EventType, ShiftQuickSelectSlots} from '@hconnect/common/types'
import {generateQueryString, getTimeRangeOrQuickSelectSlotsString} from '@hconnect/common/utils'
import {generatePath} from 'react-router'

import {
  OPTIMIZATION_SPECIALIST_PLANT_KPI,
  OPTIMIZATION_SPECIALIST_STOPPAGE_ANALYSIS,
  PROCESS_ENGINEER_PLANT_KPI,
  PROCESS_ENGINEER_STOPPAGE_ANALYSIS,
  RELIABILITY_ENGINEER_PLANT_KPI,
  RELIABILITY_ENGINEER_STOPPAGE_ANALYSIS,
  MAJOR_OUTAGE_LEAD_STOPPAGE_ANALYSIS,
  MAJOR_OUTAGE_LEAD_DOWNTIME
} from '../../shared/routes'
import {QuickSelectionId} from '../../shared/types'

const getPagePath = (
  basePath: string,
  params: Record<string, string>,
  timeRange: QuickSelectionId
) => {
  const path = generatePath(basePath, params)
  const query = new URLSearchParams({timeRange}).toString()

  return `${path}?${query}`
}

export const getReliabilityEngineerPerformancePagePath = (
  plantId: string,
  timeRange: QuickSelectionId
) => {
  return getPagePath(RELIABILITY_ENGINEER_PLANT_KPI, {plantId}, timeRange)
}

export const getProcessEngineerPerformancePagePath = (
  plantId: string,
  timeRange: QuickSelectionId
) => {
  return getPagePath(PROCESS_ENGINEER_PLANT_KPI, {plantId}, timeRange)
}

export const getOptimizationSpecialistPerformancePagePath = (
  plantId: string,
  timeRange: QuickSelectionId
) => {
  return getPagePath(OPTIMIZATION_SPECIALIST_PLANT_KPI, {plantId}, timeRange)
}

export const getProcessEngineerStoppageAnalysisPagePath = (
  plantId: string,
  equipmentId: string
) => {
  return generatePath(PROCESS_ENGINEER_STOPPAGE_ANALYSIS, {plantId, equipmentId})
}

export const getReliabilityEngineerStoppageAnalysisPagePath = (
  plantId: string,
  equipmentId: string
) => {
  return generatePath(RELIABILITY_ENGINEER_STOPPAGE_ANALYSIS, {plantId, equipmentId})
}

export const getMajorOutageLeadStoppageAnalysisPath = (plantId: string, equipmentId: string) => {
  return generatePath(MAJOR_OUTAGE_LEAD_STOPPAGE_ANALYSIS, {plantId, equipmentId})
}

export const getOptimizationSpecialistStoppageAnalysisPagePath = (
  plantId: string,
  equipmentId: string,
  timeRange: QuickSelectionId
) => {
  return getPagePath(OPTIMIZATION_SPECIALIST_STOPPAGE_ANALYSIS, {plantId, equipmentId}, timeRange)
}

export const generateCockpitShiftSummaryLink = (
  timeRange: DateRange | ShiftQuickSelectSlots,
  plantId: string
): string => {
  const query = {
    timeRange: getTimeRangeOrQuickSelectSlotsString(timeRange)
  }

  const baseUrl = appUrl(COCKPIT_HOST)
  const path = generatePath(`/:plantId/${SUMMARY_SEGMENT}`, {plantId})
  const endpoint = `${baseUrl}${path}`
  const linkPath = `${endpoint}?${generateQueryString(query)}`

  return linkPath
}

export const getMajorOutageLeadPlantDowntimePagePath = (plantId: string) => {
  return generatePath(MAJOR_OUTAGE_LEAD_DOWNTIME, {plantId})
}

const generateCockpitShiftEventLink = (plantId: string, query: Record<string, string>) => {
  const baseUrl = appUrl(COCKPIT_HOST)
  const path = generatePath(PLANT_EVENTS, {plantId})
  const queryString = generateQueryString(query)

  return `${baseUrl}${path}?${queryString}`
}

export const getCockpitShiftEventsByTypeLink = ({
  plantId,
  eventType,
  timeRange
}: {
  plantId: string
  eventType: EventType
  timeRange: ShiftQuickSelectSlots
}) => {
  const query: Record<string, string> = {
    [COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY]: EventsTab.Events,
    eventType,
    timeRange
  }
  return generateCockpitShiftEventLink(plantId, query)
}

export const generateCockpitWorkOrdersLink = (plantId: string) =>
  `${appUrl(COCKPIT_HOST)}${generatePath(PLANT_EVENTS, {
    plantId
  })}?${COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY}=${EventsTab.WorkOrders}`

export const getCockpitShiftEventByIdAndTypeLink = ({
  plantId,
  eventId,
  timeRange,
  eventType
}: {
  plantId: string
  eventType: EventType
  timeRange: ShiftQuickSelectSlots
  eventId: string
}) => {
  const query: Record<string, string> = {
    [COCKPIT_EVENTS_PAGE_TAB_PARAM_KEY]: EventsTab.Events,
    eventType,
    timeRange,
    eventId
  }
  return generateCockpitShiftEventLink(plantId, query)
}
